import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Table, Button, Popover, Spin, Space, Switch } from 'antd';
import { NumberType, SelectInput, SwitchInput, TextType } from '../adminInputs';
import {
  clearAssetEodPreview,
  clearAssetIntradayPreview,
  clearIndexFundPreview,
  deleteUnderlyingAsset,
  fetchAssets,
  fetchMarketHours,
  MarketHistory,
  Order,
  REGIONS,
  requestAssetEodPreview,
  requestAssetIntradayPreview,
  selectors,
  UnderlyingAsset,
  updateAsset,
} from 'state';
import { columnSearchWidget, LoadFailure } from 'components';
import { formatters, useModal } from 'services';
import CreateAssetModal from './CreateAssetModal';
import {
  DeleteRowOutlined,
  EyeFilled,
  EyeTwoTone,
  InfoCircleOutlined,
} from '@ant-design/icons';
import IndexFundPreviewModal from '../IndexFundsTab/IndexFundPreviewModal';
import AssetEodPreviewModal from './AssetEodPreviewModal';
import RefreshIndexFundModal from '../IndexFundsTab/RefreshIndexFundModal';
import RefreshAssetEodModal from './RefreshAssetEodModal';
import UsageTable from './UsageTable';
import {
  onFilterFunction,
  UsageColumnComponents,
  UsageFilters,
  UsageTitle,
} from './UsageColumnComponents';
import { CSVLink } from 'react-csv';
import type {
  TableColumnGroupType as ColumnGroupType,
  TableColumnType as ColumnType,
} from 'antd';
import type { FilterValue } from 'antd/es/table/interface';
import AssetIntradayPreviewModal from './AssetIntradayPreviewModal';
import { assetIntradayData } from '../../../../state/app/selectors';
import UploadVolsModal from './UploadVols';
import dayjs from 'dayjs';
import LsegEodQuota from './LsegEodQuota';
import {
  IntradayIndicator,
  useEodStatus,
  useIntradayStatus,
} from './IntradayStatus';
import IntradayTierSettings from './IntradayTierSettings';

const AssetsTab: React.FC<{ active?: boolean }> = ({ active = true }) => {
  const dispatch = useDispatch();
  const assetItems = useSelector(selectors.underlyingAssets);
  const intradayStatus = useIntradayStatus(active);
  const intradaySnapshot = intradayStatus.snapshot;
  const eodStatus = useEodStatus(active);
  const eodSnapshot = eodStatus.snapshot;
  const assets = useMemo(() => Object.values(assetItems), [assetItems]);
  const {
    fetched: assetsFetched,
    waiting: assetsWaiting,
    error: assetsError,
  } = useSelector(selectors.underlyingAssetsApi);
  const currencies = useSelector(selectors.currenciesList);

  const [CreateModal, showModal] = useModal(CreateAssetModal);
  const [showEodPreview, setShowEodPreview] = useState(false);
  const [showIntradayPreview, setShowIntradayPreview] = useState(false);
  const [showUsageTable, setShowUsageTable] = useState(false);
  const [inPlayingSessions, setInPlayingSessions] = useState<number[]>();
  const [inPausedSessions, setInPausedSessions] = useState<number[]>();
  const [previewEodLoading, setPreviewEodLoading] = useState(false);
  const [previewIntradayLoading, setPreviewIntradayLoading] = useState(false);
  const marketHours = useSelector(selectors.marketHoursItems);

  const [refreshModal, showRefreshModal] = useModal(RefreshAssetEodModal);
  const [volsModal, showVolsModal] = useModal(UploadVolsModal);

  const assetEodData = useSelector(selectors.assetEodData);
  const assetIntradayData = useSelector(selectors.assetIntradayData);

  // NEW: column visibility toggles
  const [showIssueDateCol, setShowIssueDateCol] = useState(false);
  const [showMaturityDateCol, setShowMaturityDateCol] = useState(false);
  const [tableFilters, setTableFilters] = useState<
    Record<string, FilterValue | null>
  >({});

  useEffect(() => {
    if (assetEodData) {
      setPreviewEodLoading(false);
      setShowEodPreview(true);
    } else {
      setShowEodPreview(false);
    }
  }, [assetEodData]);

  useEffect(() => {
    return () => {
      dispatch(clearAssetEodPreview({}));
    };
  }, []);

  useEffect(() => {
    if (assetIntradayData) {
      setPreviewIntradayLoading(false);
      setShowIntradayPreview(true);
    } else {
      setShowIntradayPreview(false);
    }
  }, [assetIntradayData]);

  useEffect(() => {
    return () => {
      dispatch(clearAssetIntradayPreview({}));
    };
  }, []);

  useEffect(() => {
    if (!assetsFetched && !assetsWaiting && !assetsError) {
      dispatch(fetchAssets({}));
    }
    // if (!marketHours.length) {
    //   dispatch(fetchMarketHours({}));
    // }
  }, [assetsFetched, assetsWaiting, assetsError, dispatch]);

  const update = (record: any, key: any) => (val: any) =>
    dispatch(updateAsset({ id: record.id, [key]: val }));

  const onClickUsage = (record: UnderlyingAsset) => {
    setInPlayingSessions(record.in_playing_sessions);
    setInPausedSessions(record.in_paused_sessions);
    setShowUsageTable(true);
  };

  const currencyOptions = currencies.map(c => ({
    value: c.id,
    name: c.symbol,
  }));

  const regionOptions = Object.entries(REGIONS).map(([value, name]) => ({
    value: parseInt(value),
    name,
  }));

  const cmpDate = (a?: string, b?: string, nullsLast = true) => {
    const av = a && dayjs(a).isValid() ? dayjs(a).valueOf() : null;
    const bv = b && dayjs(b).isValid() ? dayjs(b).valueOf() : null;

    if (av === null && bv === null) return 0;
    if (av === null) return nullsLast ? 1 : -1;
    if (bv === null) return nullsLast ? -1 : 1;
    return av - bv;
  };

  const issueCol: ColumnType<UnderlyingAsset> & { dataIndex: string } = {
    title: 'Issue',
    dataIndex: 'asset_issue_date',
    sorter: (a, b) =>
      cmpDate(a.asset_issue_date, b.asset_issue_date, /*nullsLast*/ true),
  };

  const maturityCol: ColumnType<UnderlyingAsset> & { dataIndex: string } = {
    title: 'Maturity',
    dataIndex: 'asset_maturity_date',
    sorter: (a, b) =>
      cmpDate(a.asset_maturity_date, b.asset_maturity_date, /*nullsLast*/ true),
  };

  // Base columns (without issue/maturity yet)
  const baseColumns: ((
    | ColumnGroupType<UnderlyingAsset>
    | ColumnType<UnderlyingAsset>
  ) & {
    dataIndex: string;
  })[] = [
    { title: 'ID', dataIndex: 'id' },
    {
      title: 'RIC',
      dataIndex: 'ric',
      render: (val, record) => TextType(val, update(record, 'ric')),
      ...columnSearchWidget({ dataIndex: 'ric' }),
      sorter: (a, b) => (a.ric < b.ric ? -1 : a.ric > b.ric ? 1 : 0),
    },
    {
      title: 'Ticker',
      dataIndex: 'ticker',
      render: (val, record) => TextType(val, update(record, 'ticker')),
      sorter: (a, b) =>
        a.ticker < b.ticker ? -1 : a.ticker > b.ticker ? 1 : 0,
      ...columnSearchWidget({ dataIndex: 'ticker' }),
    },
    {
      title: 'Risk W',
      dataIndex: 'risk_weight',
      render: (val, record) => NumberType(val, update(record, 'risk_weight')),
      sorter: (a, b) => a.risk_weight - b.risk_weight,
    },
    {
      title: 'Visible',
      dataIndex: 'visible',
      render: (val, record) => SwitchInput(val, update(record, 'visible')),
      filters: [
        { text: 'Visible', value: true },
        { text: 'Hidden', value: false },
      ],
      onFilter: (value, record) => value === record.visible,
    },
    {
      title: 'Intraday',
      dataIndex: 'intraday_fetch_eligible',
      render: (_, record) => (
        <IntradayIndicator assetId={record.id} snapshot={intradaySnapshot} />
      ),
      filters: [
        { text: 'Yes', value: true },
        { text: 'No', value: false },
        { text: 'Active sessions, no tier', value: 'active-sessions-no-tier' },
      ],
      onFilter: (value, record) => {
        if (!intradaySnapshot) return false;
        const reasons = intradaySnapshot.assets[record.id];
        if (value === 'active-sessions-no-tier')
          return Boolean(
            reasons?.has_catalogue_tier === false &&
              (reasons.playing_session_ids.length ||
                reasons.paused_session_ids.length ||
                reasons.benchmark_playing_session_ids?.length ||
                reasons.benchmark_paused_session_ids?.length),
          );
        return value === Boolean(reasons);
      },
    },
    {
      title: 'EoD',
      dataIndex: 'eod_fetch_eligible',
      render: (_, record) => (
        <IntradayIndicator
          mode="eod"
          assetId={record.id}
          snapshot={eodSnapshot}
        />
      ),
      filters: [
        { text: 'Yes', value: true },
        { text: 'No', value: false },
        { text: 'Active sessions, no tier', value: 'active-sessions-no-tier' },
      ],
      onFilter: (value, record) => {
        if (!eodSnapshot) return false;
        const reasons = eodSnapshot.assets[record.id];
        if (value === 'active-sessions-no-tier')
          return Boolean(
            reasons?.has_catalogue_tier === false &&
              (reasons.playing_session_ids.length ||
                reasons.paused_session_ids.length ||
                reasons.benchmark_playing_session_ids?.length ||
                reasons.benchmark_paused_session_ids?.length),
          );
        return value === Boolean(reasons);
      },
    },
    {
      title: UsageTitle,
      dataIndex: 'usage',
      render: (_, record) => UsageColumnComponents(record, onClickUsage),
      filters: UsageFilters,
      onFilter: (value, record) => onFilterFunction(value, record),
    },
    {
      title: 'Market History',
      dataIndex: 'last_market_history',
      render: (history?: MarketHistory) =>
        formatters.dateWithTime(history?.timestamp || ''),
    },
    {
      title: 'Base',
      dataIndex: 'base_currency',
      render: (val, record) =>
        SelectInput(val, currencyOptions, update(record, 'base_currency')),
      filters: currencies.map(currency => ({
        text: currency.symbol,
        value: currency.id,
      })),
      onFilter: (value, record) => value === record.base_currency,
    },
    {
      title: 'Var',
      dataIndex: 'variable_currency',
      render: (val, record) =>
        SelectInput(val, currencyOptions, update(record, 'variable_currency')),
      filters: currencies.map(currency => ({
        text: currency.symbol,
        value: currency.id,
      })),
      onFilter: (value, record) => value === record.variable_currency,
    },
    {
      title: 'Region',
      dataIndex: 'region',
      render: (val, record) =>
        SelectInput(val, regionOptions, update(record, 'region')),
      filters: Object.entries(REGIONS).map(([id, region]) => ({
        text: region,
        value: parseInt(id),
      })),
      onFilter: (value, record) => value === record.region,
    },
    {
      title: 'Geography',
      dataIndex: 'geography',
      render: (val, record) => TextType(val, update(record, 'geography')),
      filters: (marketHours || [])
        .slice()
        .sort((a, b) => a.geography.localeCompare(b.geography))
        .map(mh => ({ text: mh.geography, value: mh.geography })),
      onFilter: (value, record) => value === record.geography,
    },
    {
      title: 'Asset Class',
      dataIndex: 'resourcetype',
      filters: Array.from(new Set(assets.map(asset => asset.resourcetype))).map(
        resourcetype => ({ text: resourcetype, value: resourcetype }),
      ),
      onFilter: (value, record) => value === record.resourcetype,
    },
    // (Issue/Maturity will be injected here conditionally)
    {
      title: 'Name',
      dataIndex: 'name',
      render: (val, record) => TextType(val, update(record, 'name')),
      sorter: (a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0),
      ...columnSearchWidget({ dataIndex: 'name' }),
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      render: (_, record) => (
        <div>
          <Popover content={'Delete asset'}>
            <Button
              danger
              onClick={() => {
                dispatch(deleteUnderlyingAsset({ id: record.id }));
              }}
            >
              <DeleteRowOutlined />
            </Button>
          </Popover>
          <Popover content={'Show EoD'}>
            <Button
              style={{ marginLeft: 5 }}
              onClick={() => {
                setPreviewEodLoading(true);
                dispatch(requestAssetEodPreview({ asset_id: record.id }));
              }}
            >
              {previewEodLoading ? <Spin /> : <EyeTwoTone />}
            </Button>
          </Popover>
          <Popover content={'Last 24h intraday'}>
            <Button
              style={{ marginLeft: 5 }}
              onClick={() => {
                setPreviewIntradayLoading(true);
                dispatch(requestAssetIntradayPreview({ asset_id: record.id }));
              }}
            >
              {previewIntradayLoading ? <Spin /> : <EyeFilled />}
            </Button>
          </Popover>
        </div>
      ),
    },
  ];

  // Inject the toggleable columns right before 'Name'
  const nameIndex = baseColumns.findIndex(c => c.dataIndex === 'name');
  const columns = [
    ...baseColumns.slice(0, nameIndex),
    ...(showIssueDateCol ? [issueCol] : []),
    ...(showMaturityDateCol ? [maturityCol] : []),
    ...baseColumns.slice(nameIndex),
  ];

  // Reuse the table's predicates so edits and status polls also update the count.
  const activeColumnFilters = columns.flatMap(column => {
    const values = tableFilters[String(column.key ?? column.dataIndex)];
    if (!values?.length || !('onFilter' in column) || !column.onFilter)
      return [];
    const { onFilter } = column;
    return [
      (asset: UnderlyingAsset) => values.some(value => onFilter(value, asset)),
    ];
  });
  const filteredAssetCount = activeColumnFilters.length
    ? assets.reduce(
        (count, asset) =>
          count + Number(activeColumnFilters.every(matches => matches(asset))),
        0,
      )
    : assets.length;

  const columnsToExclude = ['actions', 'usage'];
  const csvColumns = columns.filter(column => {
    return !columnsToExclude.includes(column.dataIndex);
  });
  csvColumns.push({
    title: 'In playing sesssions',
    dataIndex: 'in_playing_sessions',
  });
  csvColumns.push({
    title: 'In paused sesssions',
    dataIndex: 'in_paused_sessions',
  });

  const csvData = useMemo(() => {
    const titles = csvColumns.map(col => col.title);
    const body = assets.map(asset =>
      csvColumns.map(col => {
        if (col.dataIndex === 'intraday_fetch_eligible') {
          return intradaySnapshot
            ? Boolean(intradaySnapshot.assets[asset.id])
            : '';
        }
        if (col.dataIndex === 'eod_fetch_eligible') {
          return eodSnapshot ? Boolean(eodSnapshot.assets[asset.id]) : '';
        }
        const value = asset[col.dataIndex as keyof typeof asset];
        if (col.dataIndex.includes('currency')) {
          return currencyOptions.find(op => op.value === value)?.name;
        } else if (col.dataIndex === 'last_market_history') {
          return value
            ? formatters.dateWithTime((value as MarketHistory)?.timestamp)
            : '--';
        } else if (col.dataIndex === 'region') {
          return value ? REGIONS[value as keyof typeof REGIONS] : '--';
        } else {
          return value;
        }
      }),
    );
    body.unshift(titles as string[]);
    return body;
  }, [assets, columns, intradaySnapshot, eodSnapshot]); // include columns so CSV header reflects toggles

  if (assetsError) {
    return (
      <LoadFailure subject="Assets" onRetry={() => dispatch(fetchAssets({}))} />
    );
  }

  return (
    <>
      {volsModal}
      {refreshModal}
      {CreateModal}
      {showEodPreview && (
        <AssetEodPreviewModal
          visible={showEodPreview}
          hide={() => setShowEodPreview(false)}
        />
      )}
      {showIntradayPreview && (
        <AssetIntradayPreviewModal
          visible={showIntradayPreview}
          hide={() => setShowIntradayPreview(false)}
        />
      )}
      {showUsageTable && (
        <UsageTable
          visible={showUsageTable}
          hide={() => setShowUsageTable(false)}
          playingSessions={inPlayingSessions}
          pausedSessions={inPausedSessions}
        />
      )}

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 16,
          flexWrap: 'wrap',
        }}
      >
        <Space wrap size="middle">
          <Button type="primary" onClick={() => showModal({})}>
            Create
          </Button>
          <Button onClick={() => showRefreshModal({})}>Refresh EoD</Button>
          <Popover
            placement="right"
            content={
              'Users already logged in will not receive geography asset updates (including hours) without refreshing the page'
            }
          >
            <InfoCircleOutlined />
          </Popover>
          <Space wrap>
            <span>Columns:</span>
            <Switch
              checked={showIssueDateCol}
              onChange={setShowIssueDateCol}
              checkedChildren="Issue ON"
              unCheckedChildren="Issue OFF"
            />
            <Switch
              checked={showMaturityDateCol}
              onChange={setShowMaturityDateCol}
              checkedChildren="Maturity ON"
              unCheckedChildren="Maturity OFF"
            />
            <Popover
              content={`Assets selected for scheduled EoD pricing, including required FX spots and CDS/repo daily capture. ${eodSnapshot?.eligible_retired_asset_count || 0} eligible retired assets are included in this count but absent from the table.`}
            >
              <span aria-live="polite" style={{ whiteSpace: 'nowrap' }}>
                EoD fetch count:{' '}
                <strong>{eodSnapshot?.eligible_asset_count ?? '—'}</strong>
              </span>
            </Popover>
            <IntradayTierSettings
              mode="eod"
              onSaved={eodStatus.refreshAfterTierSave}
            />
            <Popover
              content={`Assets selected for scheduled intraday pulling. ${
                intradaySnapshot?.eligible_retired_asset_count || 0
              } eligible retired assets are included in this count but absent from the table.`}
            >
              <span aria-live="polite" style={{ whiteSpace: 'nowrap' }}>
                Intraday fetch count:{' '}
                <strong>{intradaySnapshot?.eligible_asset_count ?? '—'}</strong>
              </span>
            </Popover>
            <IntradayTierSettings
              onSaved={() => {
                intradayStatus.refreshAfterTierSave();
                eodStatus.refreshAfterTierSave();
              }}
            />
            <LsegEodQuota />
          </Space>
        </Space>
        <Button>
          <CSVLink
            filename={'PM_prod_assets'}
            data={csvData}
            style={{ color: 'inherit', textDecoration: 'none' }}
          >
            Download CSV
          </CSVLink>
        </Button>
      </div>

      {(intradayStatus.error || intradayStatus.stale) && (
        <div role="status">
          {intradayStatus.error ||
            'Intraday status is awaiting a fresh update.'}
          {intradaySnapshot &&
            ` Last updated: ${formatters.dateWithTime(intradaySnapshot.computed_at)}.`}
        </div>
      )}

      {(eodStatus.error || eodStatus.stale) && (
        <div role="status">
          {eodStatus.error || 'EoD status is awaiting a fresh update.'}
          {eodSnapshot &&
            ` Last updated: ${formatters.dateWithTime(eodSnapshot.computed_at)}.`}
        </div>
      )}

      <div aria-live="polite" style={{ margin: '12px 0 8px' }}>
        ({assetsFetched ? filteredAssetCount : '—'} assets filtered)
      </div>
      <Table
        loading={assetsWaiting || !assetsFetched}
        dataSource={assets}
        rowKey="id"
        pagination={{ defaultPageSize: 50 }}
        columns={columns}
        onChange={(_, filters) => setTableFilters(filters)}
      />
    </>
  );
};

export default AssetsTab;
